import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import {
  listPasskeys,
  registerPasskey,
  renamePasskey,
  deletePasskey,
  type Passkey,
} from "../api/passkeys.js";
import { webauthnSupported, devicePasskeyName, passkeyFailureMessage } from "../api/webauthn.js";
import { listOwnAudit, typeLabel, type AuditEvent } from "../api/audit.js";
import { signOutEverywhere, signOut } from "../api/auth.js";
import { erasureAvailable } from "../api/account.js";
import { humanError } from "../api/errors.js";
import { navigate } from "../nav.js";
import type { BnColumn, BnRow } from "../components/index.js";

/**
 * page-security is the account security view (route /security). Today it
 * manages passkeys: a table of the caller's registered credentials (with a
 * Synced vs Device-bound badge from the authenticator's backup flag) and
 * add/rename/remove flows. Adding runs the full browser WebAuthn ceremony
 * with a device-derived default name; removing is always allowed — even the
 * last passkey — because the email-code sign-in remains, so the confirm copy
 * reassures rather than warns about lockout. Every color/space/radius/font
 * comes from a --bino-* token; all API access goes through the typed
 * passkeys client.
 *
 * Below the passkeys sits the "Recent activity" card: the caller's own auth
 * audit feed (GET /v1/audit) — sign-ins, credential changes, API-key events —
 * keyset-paginated via a "Load more" button. The two cards load and fail
 * independently: an audit outage must not hide passkey management.
 *
 * When the server offers data export and account deletion, a "Your data" card
 * links to /account, where both live. It is only a link, so the card is shown
 * only after the probe finds those routes; without them nothing hints at it.
 *
 * Last comes "Sign out everywhere", the recovery lever for a stolen laptop or a
 * shared browser left signed in. It is deliberately the final card: it is the
 * only destructive action here and it ends the current session too.
 */
@customElement("page-security")
export class PageSecurity extends LitElement {
  @state() private passkeys: Passkey[] = [];
  @state() private loading = true;
  @state() private loadError = "";

  // Add flow: modal open, name form value, and the in-flight flag (which
  // spans the browser's authenticator prompt, so it can be user-paced).
  @state() private addOpen = false;
  @state() private addName = "";
  @state() private adding = false;

  // Rename flow: the passkey being renamed, the edited value, in-flight flag.
  @state() private renameTarget: Passkey | null = null;
  @state() private renameName = "";
  @state() private renaming = false;

  // Remove flow: the passkey pending confirmation and the in-flight flag.
  @state() private removeTarget: Passkey | null = null;
  @state() private removing = false;

  // Audit feed: the rows loaded so far, the cursor for older events (empty
  // when exhausted), and separate initial/error/load-more states so paging
  // failures toast without blanking rows that already rendered.
  @state() private audit: AuditEvent[] = [];
  @state() private auditLoading = true;
  @state() private auditError = "";
  @state() private auditNextBefore = "";
  @state() private auditMoreLoading = false;

  /** Whether the server offers data export and account deletion. */
  @state() private erasureOn = false;

  // Sign-out-everywhere flow: confirm modal open + in-flight flag.
  @state() private signOutAllOpen = false;
  @state() private signingOutAll = false;

  /** Static per-browser capability; without it the add flow is hidden. */
  private readonly passkeySupport = webauthnSupported();

  static override styles = css`
    :host {
      display: block;
    }
    .head {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1;
      min-width: 0;
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .load-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--bino-space-md);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      text-align: center;
      font-family: var(--bino-font-sans);
      color: var(--bino-text-secondary);
    }
    .row-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
    }
    .unsupported {
      margin: 0;
      padding: var(--bino-space-lg);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .confirm-name {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    .modal-note {
      margin: var(--bino-space-md) 0 0;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .load-more {
      display: flex;
      justify-content: center;
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-top: 1px solid var(--bino-border);
    }
    .card-copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 60ch;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .modal-copy {
      margin: 0 0 var(--bino-space-md);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .modal-copy:last-child {
      margin-bottom: 0;
    }
    .modal-copy strong {
      color: var(--bino-text);
      font-weight: 600;
    }
    .modal-copy a {
      color: var(--bino-text);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
    void this.loadAudit();
    void this.probeErasure();
  }

  private async probeErasure() {
    this.erasureOn = await erasureAvailable();
  }

  private async load() {
    this.loading = true;
    this.loadError = "";
    try {
      this.passkeys = await listPasskeys();
    } catch (e) {
      this.loadError = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  // ---- Add -------------------------------------------------------------------

  private openAdd() {
    // Prefill with a recognizable device-derived default ("Chrome on macOS")
    // so an unedited submit still yields a list entry you can tell apart.
    this.addName = devicePasskeyName();
    this.addOpen = true;
  }

  private closeAdd() {
    if (this.adding) return;
    this.addOpen = false;
  }

  private async submitAdd() {
    const name = this.addName.trim();
    if (name === "" || this.adding) return;
    // Enforce the server's 100-char name cap BEFORE the authenticator prompt:
    // failing at finish would leave a phantom resident credential in the
    // user's password manager that the server never stored.
    if (name.length > 100) {
      toast("Passkey names are limited to 100 characters.", "error");
      return;
    }
    this.adding = true;
    try {
      await registerPasskey(name);
      this.addOpen = false;
      toast("Passkey added", "success");
      // Reload instead of appending: created/backedUp come from the server's
      // view of the finished ceremony, not from anything we have locally.
      await this.load();
    } catch (err) {
      // A dismissed authenticator prompt is a cancel: keep the modal open so
      // the user can retry or close it, and say nothing. Real failures
      // (server rejection, RP-ID mismatch, already-registered device) toast.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.adding = false;
    }
  }

  // ---- Rename ----------------------------------------------------------------

  private openRename(p: Passkey) {
    this.renameTarget = p;
    this.renameName = p.name;
  }

  private closeRename() {
    if (this.renaming) return;
    this.renameTarget = null;
  }

  private async submitRename() {
    const target = this.renameTarget;
    const name = this.renameName.trim();
    if (!target || name === "" || this.renaming) return;
    this.renaming = true;
    try {
      await renamePasskey(target.id, name);
      this.passkeys = this.passkeys.map((p) => (p.id === target.id ? { ...p, name } : p));
      this.renameTarget = null;
      toast("Passkey renamed", "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.renaming = false;
    }
  }

  // ---- Remove ----------------------------------------------------------------

  private async confirmRemove() {
    const target = this.removeTarget;
    if (!target || this.removing) return;
    this.removing = true;
    try {
      await deletePasskey(target.id);
      this.passkeys = this.passkeys.filter((p) => p.id !== target.id);
      this.removeTarget = null;
      toast("Passkey removed", "success");
    } catch (err) {
      toast(humanError(err), "error");
    } finally {
      this.removing = false;
    }
  }

  // ---- Audit feed ------------------------------------------------------------

  private async loadAudit() {
    this.auditLoading = true;
    this.auditError = "";
    try {
      const page = await listOwnAudit({ limit: 20 });
      this.audit = page.items;
      this.auditNextBefore = page.nextBefore;
    } catch (err) {
      this.auditError = humanError(err);
    } finally {
      this.auditLoading = false;
    }
  }

  private async loadMoreAudit() {
    if (this.auditNextBefore === "" || this.auditMoreLoading) return;
    this.auditMoreLoading = true;
    try {
      const page = await listOwnAudit({ before: this.auditNextBefore, limit: 20 });
      this.audit = [...this.audit, ...page.items];
      this.auditNextBefore = page.nextBefore;
    } catch (err) {
      // Paging failures toast instead of replacing the card: the rows already
      // on screen are still valid, only the "older" fetch failed.
      toast(humanError(err), "error");
    } finally {
      this.auditMoreLoading = false;
    }
  }

  // ---- Sign out everywhere ---------------------------------------------------

  private closeSignOutAll() {
    if (this.signingOutAll) return;
    this.signOutAllOpen = false;
  }

  private async confirmSignOutAll() {
    if (this.signingOutAll) return;
    this.signingOutAll = true;
    try {
      // Server first: only once it confirms do we drop the local session. The
      // token we just used is dead now, so clearing the store is bookkeeping —
      // but doing it in the other order would sign this tab out even when the
      // revocation failed, leaving the other devices live.
      await signOutEverywhere();
      toast("Signed out on all devices", "success");
      signOut();
      navigate("/login");
    } catch (err) {
      toast(humanError(err), "error");
      this.signingOutAll = false;
    }
    // No finally: on success the page is navigating away, and resetting state
    // on a torn-down element would only re-render a modal nobody sees.
  }

  // ---- Formatting ------------------------------------------------------------

  private fmtDate(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  /** fmtWhen renders a compact relative time ("5m ago") while it is meaningful
   *  and falls back to an absolute date for events older than a week. */
  private fmtWhen(iso: string): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return this.fmtDate(iso);
  }

  // ---- Render ----------------------------------------------------------------

  private renderTable() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "kind", label: "Type" },
      { key: "created", label: "Created" },
      { key: "lastUsed", label: "Last used" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.passkeys.map((p) => ({
      name: p.name,
      // "Synced" = backed up to a passkey provider (usable on other devices);
      // "Device-bound" = lives only where it was created.
      kind: p.backedUp
        ? html`<bn-badge variant="info">Synced</bn-badge>`
        : html`<bn-badge variant="neutral">Device-bound</bn-badge>`,
      created: this.fmtDate(p.created),
      lastUsed: p.lastUsed
        ? this.fmtDate(p.lastUsed)
        : html`<bn-badge variant="neutral">Never</bn-badge>`,
      actions: html`<span class="row-actions">
        <bn-button
          variant="secondary"
          size="sm"
          @click=${() => this.openRename(p)}
          aria-label=${"Rename passkey " + p.name}
          >Rename</bn-button
        >
        <bn-button
          variant="danger"
          size="sm"
          @click=${() => (this.removeTarget = p)}
          aria-label=${"Remove passkey " + p.name}
          >Remove</bn-button
        >
      </span>`,
    }));
    return html`<bn-table .columns=${columns} .rows=${rows} empty="No passkeys yet."></bn-table>`;
  }

  private renderPasskeys() {
    if (this.loading) {
      return html`<bn-card flush><bn-spinner block label="Loading passkeys…"></bn-spinner></bn-card>`;
    }
    if (this.loadError) {
      return html`<bn-card flush>
        <div class="load-error">
          <span>${this.loadError}</span>
          <bn-button variant="secondary" size="sm" @click=${() => void this.load()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    if (!this.passkeySupport && this.passkeys.length === 0) {
      return html`<bn-card flush heading="Passkeys">
        <p class="unsupported">
          This browser does not support passkeys. Use another browser to add one.
        </p>
      </bn-card>`;
    }
    if (this.passkeys.length === 0) {
      return html`<bn-card flush>
        <bn-empty-state
          icon="🔐"
          heading="No passkeys yet"
          message="Add a passkey to sign in with your fingerprint, face, or device PIN instead of an email code."
        >
          <bn-button slot="actions" variant="primary" @click=${() => this.openAdd()}
            >Add passkey</bn-button
          >
        </bn-empty-state>
      </bn-card>`;
    }
    return html`<bn-card flush heading="Passkeys">
      ${this.passkeySupport
        ? html`<bn-button slot="actions" variant="primary" size="sm" @click=${() => this.openAdd()}
            >Add passkey</bn-button
          >`
        : nothing}
      ${this.renderTable()}
    </bn-card>`;
  }

  private renderAudit() {
    if (this.auditLoading) {
      return html`<bn-card flush><bn-spinner block label="Loading activity…"></bn-spinner></bn-card>`;
    }
    if (this.auditError) {
      return html`<bn-card flush>
        <div class="load-error">
          <span>${this.auditError}</span>
          <bn-button variant="secondary" size="sm" @click=${() => void this.loadAudit()}
            >Retry</bn-button
          >
        </div>
      </bn-card>`;
    }
    const columns: BnColumn[] = [
      { key: "event", label: "Event" },
      { key: "ip", label: "IP address", mono: true },
      { key: "when", label: "When", align: "right" },
    ];
    // ip may be empty: some events (e.g. membership changes via model hooks)
    // are recorded without request context.
    const rows: BnRow[] = this.audit.map((ev) => ({
      event: typeLabel(ev.type),
      ip: ev.ip || "—",
      when: this.fmtWhen(ev.created),
    }));
    return html`<bn-card flush heading="Recent activity">
      <bn-table
        .columns=${columns}
        .rows=${rows}
        empty="No activity recorded yet. Sign-ins and security changes will show up here."
      ></bn-table>
      ${this.auditNextBefore !== ""
        ? html`<div class="load-more">
            <bn-button
              variant="secondary"
              size="sm"
              ?loading=${this.auditMoreLoading}
              @click=${() => void this.loadMoreAudit()}
              >Load more</bn-button
            >
          </div>`
        : nothing}
    </bn-card>`;
  }

  private renderYourData() {
    return html`<bn-card heading="Your data">
      <p class="card-copy">
        Download a copy of your account data, or delete your account and the teams only you belong
        to.
      </p>
      <bn-button variant="secondary" @click=${() => navigate("/account")}
        >Manage your data</bn-button
      >
    </bn-card>`;
  }

  private renderSignOutAll() {
    return html`<bn-card heading="Sign out everywhere">
      <p class="card-copy">
        Signs you out in every browser and on every device, this one included. Use it if you lost
        a device or signed in on a computer you don't control.
      </p>
      <bn-button variant="danger" @click=${() => (this.signOutAllOpen = true)}
        >Sign out everywhere</bn-button
      >
    </bn-card>`;
  }

  private renderSignOutAllModal() {
    return html`<bn-modal
      ?open=${this.signOutAllOpen}
      heading="Sign out everywhere?"
      @close=${() => this.closeSignOutAll()}
    >
      <p class="modal-copy">
        Every device signed in to your account will be signed out, <strong>including this one</strong
        >. You'll need to sign in again here.
      </p>
      <p class="modal-copy">
        Short-lived tokens made from your API keys stop working at once. The API keys stay valid.
        Use them to get a new token, or revoke them on the
        <a href="/api-keys">API keys</a> page.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeSignOutAll()}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.signingOutAll}
        @click=${() => void this.confirmSignOutAll()}
        >Sign out everywhere</bn-button
      >
    </bn-modal>`;
  }

  private renderAddModal() {
    return html`<bn-modal
      ?open=${this.addOpen}
      heading="Add a passkey"
      @close=${() => this.closeAdd()}
    >
      <bn-field
        label="Passkey name"
        required
        hint="Helps you tell your passkeys apart."
      >
        <bn-input
          .value=${this.addName}
          autocomplete="off"
          ?disabled=${this.adding}
          @input=${(e: CustomEvent) => (this.addName = (e.detail as { value: string }).value)}
        ></bn-input>
      </bn-field>
      <p class="modal-note">
        Your browser will ask you to confirm with your fingerprint, face, or device PIN.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeAdd()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.adding}
        ?disabled=${this.addName.trim() === ""}
        @click=${() => void this.submitAdd()}
        >Add passkey</bn-button
      >
    </bn-modal>`;
  }

  private renderRenameModal() {
    const target = this.renameTarget;
    return html`<bn-modal
      ?open=${target !== null}
      heading="Rename passkey"
      @close=${() => this.closeRename()}
    >
      <bn-field label="Passkey name" required>
        <bn-input
          .value=${this.renameName}
          autocomplete="off"
          ?disabled=${this.renaming}
          @input=${(e: CustomEvent) => (this.renameName = (e.detail as { value: string }).value)}
        ></bn-input>
      </bn-field>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeRename()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.renaming}
        ?disabled=${this.renameName.trim() === ""}
        @click=${() => void this.submitRename()}
        >Rename</bn-button
      >
    </bn-modal>`;
  }

  private renderRemoveModal() {
    const target = this.removeTarget;
    return html`<bn-modal
      ?open=${target !== null}
      heading="Remove passkey?"
      @close=${() => {
        if (!this.removing) this.removeTarget = null;
      }}
    >
      ${target
        ? html`<p>
            <span class="confirm-name">${target.name}</span> will stop working for sign-in
            immediately. You can still sign in with an email code.
          </p>`
        : nothing}
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => {
          if (!this.removing) this.removeTarget = null;
        }}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.removing}
        @click=${() => void this.confirmRemove()}
        >Remove passkey</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    return html`
      <div class="head">
        <div class="head-text">
          <h1>Security</h1>
        </div>
      </div>

      <div class="stack">
        ${this.renderPasskeys()} ${this.renderAudit()}
        ${this.erasureOn ? this.renderYourData() : nothing} ${this.renderSignOutAll()}
      </div>

      ${this.renderAddModal()} ${this.renderRenameModal()} ${this.renderRemoveModal()}
      ${this.renderSignOutAllModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-security": PageSecurity;
  }
}
